import { useUserValue } from "$root/functional-areas/user";
import { useLocation } from "react-router";
import { hasAccess } from "../AuthorizationGuard";
import { typedUrlForRoute } from "../PlatformRouter/RoutesDef";
import { HierarchicalLinksModule } from "./HierarchicalLinksModule";
import type { AppDrawerModuleItem } from "./AppDrawer-shared";
import { PortfolioStudioSettingTabEnum } from "$root/functional-areas/portfolio-studio-settings/tab-enum";
import type { IconName } from "@mdotm/shadcn/legacy/components";
import { qualifier } from "$root/utils";

export function SettingsModule() {
	return <HierarchicalLinksModule className="px-4" items={useSettingsItems()} />;
}

export function useSettingsItems(): AppDrawerModuleItem<
	| "InstrumentCustomisation"
	| (typeof PortfolioStudioSettingTabEnum)["MarketViewSetting"]
	| (typeof PortfolioStudioSettingTabEnum)["ExplainabilitySettings"]
	| "Notification settings"
>[] {
	const user = useUserValue();
	const location = useLocation();

	return [
		{
			qualifier: qualifier.appDrawer.settingsModules("InstrumentCustomisation"),
			icon: "instruments-customisation" satisfies IconName,
			enabled: true,
			name: "InstrumentCustomisation",
			title: "Instr. customization",
			href: typedUrlForRoute("PortfolioStudioSettings/InstrumentCustomisation", {}),
			active: location.pathname.startsWith(typedUrlForRoute("PortfolioStudioSettings/InstrumentCustomisation", {})),
		},
		{
			qualifier: qualifier.appDrawer.settingsModules("MarketViewSettings"),
			icon: "market-view" satisfies IconName,
			enabled: hasAccess(user, { requiredService: "CUSTOM_MARKET_VIEW_SETTINGS_EDITOR" }),
			name: PortfolioStudioSettingTabEnum.MarketViewSetting,
			title: "Market view settings",
			href: typedUrlForRoute("PortfolioStudioSettings", {
				tab: PortfolioStudioSettingTabEnum.MarketViewSetting,
			}),
			active:
				location.pathname.startsWith("/portfolio_studio_settings") &&
				location.search.includes(`tab=${PortfolioStudioSettingTabEnum.MarketViewSetting}`),
		},
		{
			qualifier: qualifier.appDrawer.settingsModules("ExplainabilityOptions"),
			icon: "explainability" satisfies IconName,
			enabled: hasAccess(user, { requiredService: "CUSTOM_MARKET_VIEW_SETTINGS_EDITOR" }),
			name: PortfolioStudioSettingTabEnum.ExplainabilitySettings,
			title: "Explainability options",
			href: typedUrlForRoute("PortfolioStudioSettings", {
				tab: PortfolioStudioSettingTabEnum.ExplainabilitySettings,
			}),
			active:
				location.pathname.startsWith("/portfolio_studio_settings") &&
				location.search.includes(`tab=${PortfolioStudioSettingTabEnum.ExplainabilitySettings}`),
		},
		{
			qualifier: qualifier.appDrawer.settingsModules("NotificationSettings"),
			icon: "Property-1Notifications" satisfies IconName,
			enabled: hasAccess(user, { requiredService: "INVESTMENTS" }) || hasAccess(user, { requiredService: "REPORTS" }),
			name: "Notification settings",
			title: "Notification settings",
			href: typedUrlForRoute("Notification", {}),
			active: location.pathname.startsWith(typedUrlForRoute("Notification", {})),
		},
	];
}
